/**
 * Motion primitives (§13.1: "an odometer count-up on the Real Cashback
 * headline, smooth layout transitions… Respect prefers-reduced-motion").
 *
 * The odometer never does arithmetic on money. It rolls each character of an
 * already-formatted string into place, so the figure on screen is exactly the
 * string `formatMoney` produced from the API's decimal — including during the
 * animation, where a digit is only ever one of 0-9 on its way to the right one.
 * A float-based count-up would show ₹9,842.5999 for a frame; this cannot.
 */

import { useReducedMotion } from "framer-motion";
import * as React from "react";

import { cn } from "@/lib/cn";

// --- Odometer ----------------------------------------------------------------

const DIGITS = "0123456789";

export function Odometer({
  value,
  className,
  minorClassName = "figure-minor",
  delay = 0,
  splitMinor = true,
}: {
  /** The formatted figure, e.g. "₹9,842.60". Rendered exactly. */
  value: string;
  className?: string;
  /** Applied to the part after the decimal point (the paise). */
  minorClassName?: string;
  /** Seconds before the roll starts — lets it sit inside a load stagger. */
  delay?: number;
  splitMinor?: boolean;
}) {
  const reduce = useReducedMotion();
  const [armed, setArmed] = React.useState(false);

  React.useEffect(() => {
    // Two frames: the first paints every column at 0, the second sets the
    // target, so the transition has a start state to run from.
    const id = requestAnimationFrame(() => requestAnimationFrame(() => setArmed(true)));
    return () => cancelAnimationFrame(id);
  }, []);

  const dot = splitMinor ? value.lastIndexOf(".") : -1;
  const major = dot >= 0 ? value.slice(0, dot) : value;
  const minor = dot >= 0 ? value.slice(dot) : "";

  if (reduce) {
    return (
      <span className={className}>
        {major}
        {minor && <span className={minorClassName}>{minor}</span>}
      </span>
    );
  }

  const chars = [...major];
  return (
    <span className={cn("inline-flex items-baseline", className)} aria-label={value} role="text">
      <span aria-hidden className="inline-flex">
        {chars.map((ch, i) => (
          <Char key={i} ch={ch} armed={armed} delay={delay + i * 0.045} />
        ))}
      </span>
      {minor && (
        <span aria-hidden className={cn("inline-flex", minorClassName)}>
          {[...minor].map((ch, i) => (
            <Char key={i} ch={ch} armed={armed} delay={delay + (chars.length + i) * 0.045} />
          ))}
        </span>
      )}
    </span>
  );
}

function Char({ ch, armed, delay }: { ch: string; armed: boolean; delay: number }) {
  const d = DIGITS.indexOf(ch);
  if (d < 0) return <span>{ch}</span>;
  return (
    <span className="relative inline-block h-[1em] overflow-hidden leading-none" style={{ lineHeight: 1 }}>
      {/* Invisible copy sets the column's width to this digit's width. */}
      <span className="invisible">{ch}</span>
      <span
        className="absolute inset-x-0 top-0 flex flex-col"
        style={{
          transform: `translateY(-${armed ? d : 0}em)`,
          transition: `transform 1.6s cubic-bezier(0.16, 1, 0.3, 1) ${delay}s`,
        }}
      >
        {[...DIGITS].map((n) => (
          <span key={n} className="block h-[1em] text-center leading-none">
            {n}
          </span>
        ))}
      </span>
    </span>
  );
}

// --- Tilt --------------------------------------------------------------------

/**
 * A card that leans toward the pointer, with a specular highlight that follows
 * it (the `--mx`/`--my` custom properties read by `.card-object::before`).
 * Off for touch and for reduced motion: there it is a flat card with a sheen.
 */
export function useTilt<T extends HTMLElement>(strength = 1) {
  const ref = React.useRef<T>(null);
  const reduce = useReducedMotion();

  React.useEffect(() => {
    const el = ref.current;
    if (!el || reduce) return;
    if (window.matchMedia("(pointer: coarse)").matches) return;
    const target = el.querySelector<HTMLElement>(".card-object") ?? el;
    let frame = 0;

    const move = (e: PointerEvent) => {
      const r = target.getBoundingClientRect();
      const px = (e.clientX - r.left) / r.width;
      const py = (e.clientY - r.top) / r.height;
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        target.style.transform = `perspective(1100px) rotateY(${(px - 0.5) * 14 * strength}deg) rotateX(${(0.5 - py) * 10 * strength}deg) translateZ(0) scale(1.015)`;
        target.style.setProperty("--mx", `${px * 100}%`);
        target.style.setProperty("--my", `${py * 100}%`);
      });
    };
    const leave = () => {
      cancelAnimationFrame(frame);
      target.style.transform = "";
      target.style.removeProperty("--mx");
      target.style.removeProperty("--my");
    };

    el.addEventListener("pointermove", move);
    el.addEventListener("pointerleave", leave);
    return () => {
      cancelAnimationFrame(frame);
      el.removeEventListener("pointermove", move);
      el.removeEventListener("pointerleave", leave);
    };
  }, [reduce, strength]);

  return ref;
}

// --- Stagger -----------------------------------------------------------------

/** Delay for the nth item of a load stagger, capped so long lists don't drag. */
export function stagger(index: number, base = 0, step = 55, cap = 10): React.CSSProperties {
  return { animationDelay: `${base + Math.min(index, cap) * step}ms` };
}

// --- Live dot ----------------------------------------------------------------

export function PulseDot({
  tone = "paid",
  className,
}: {
  tone?: "paid" | "live" | "warn" | "overdue";
  className?: string;
}) {
  const color = { paid: "bg-paid", live: "bg-live", warn: "bg-warn", overdue: "bg-overdue" }[tone];
  return (
    <span
      aria-hidden
      className={cn("inline-block h-1.5 w-1.5 shrink-0 animate-ping rounded-full", color, className)}
      style={{ "--ping-color": `var(--${tone})` } as React.CSSProperties}
    />
  );
}

/** Wraps a `CardObject` so it leans toward the pointer. */
export function Tilt({ children, className }: { children: React.ReactNode; className?: string }) {
  const ref = useTilt<HTMLDivElement>();
  return (
    <div ref={ref} className={cn("group [perspective:1100px]", className)}>
      {children}
    </div>
  );
}
